<template>
    <DataTable
        ref="DataTable"
        :header="$t('deviationsFromRecommendedPriceInTime')"
        :fields="fields"
        :items="timeChartConfigurations"
        :loading="request.getLoading()"
        :hover="false"
        class="mb-4"
        thead-class="d-none"
        table-class="table-layout-fixed"
        :overflow="false"
        :per-page-values="[10, 20, 50, null]"
        :per-page.sync="currentNumberShown"
    >
        <template #headerSide>
            <div :key="spaceId" class="d-flex gap-3">
                <DropdownFilter v-model="currentNumberShown" :items="[10, 20, 50, null]" :prefix="`${$t('show')}: `" :disabled="request.getLoading()" />
                <DropdownFilter v-model="currentSortBy" :items="['summary', 'percentDiff']" :prefix="`${$t('sortBy')}: `" :disabled="request.getLoading()" />
                <DropdownFilter v-model="currentGroupBy" :items="['day', 'month', 'quarter', 'year']" :prefix="`${$t('range')}: `" :disabled="request.getLoading()" />
            </div>
        </template>
        <template #cell(chart)="{ item }">
            <EChart :options="item.chartOptions" :group-by="currentGroupBy" type="time" />
        </template>
    </DataTable>
</template>



<script>
    import EChart from '@/components/common/EChart.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import { startOfDay, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';

    export default {
        name: 'DeviationsFromRecommendedPriceInTime',
        components: {
            EChart,
            DataTable,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                currentNumberShown: 10,
                currentSortBy: 'summary',
                currentGroupBy: 'day',
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            fields() {
                return [{ key: 'chart', label: '' }];
            },
            rawProductsData() {
                return this.parentComponent.data?.products || {};
            },
            timeChartConfigurations() {
                const products = Object.values(this.rawProductsData);

                const getBucketStartTimestamp = (timestampMs, groupBy) => {
                    const originalDate = new Date(Number(timestampMs));
                    switch (groupBy) {
                        case 'day':
                            return startOfDay(originalDate).getTime();
                        case 'month':
                            return startOfMonth(originalDate).getTime();
                        case 'quarter':
                            return startOfQuarter(originalDate).getTime();
                        case 'year':
                            return startOfYear(originalDate).getTime();
                        default:
                            return startOfDay(originalDate).getTime();
                    }
                };

                const buildFullBuckets = (startTs, endTs, groupBy) => {
                    if (startTs == null || endTs == null) return [];
                    const buckets = [];
                    const stepFn =
                        {
                            day: d => d.setDate(d.getDate() + 1),
                            month: d => d.setMonth(d.getMonth() + 1),
                            quarter: d => d.setMonth(d.getMonth() + 3),
                            year: d => d.setFullYear(d.getFullYear() + 1),
                        }[groupBy] || (d => d.setDate(d.getDate() + 1));

                    const cursor = new Date(getBucketStartTimestamp(startTs, groupBy));
                    const endAligned = getBucketStartTimestamp(endTs, groupBy);

                    while (cursor.getTime() <= endAligned) {
                        buckets.push(getBucketStartTimestamp(cursor.getTime(), groupBy));
                        stepFn(cursor);
                    }
                    return buckets;
                };

                const isPercentMode = this.currentSortBy === 'percentDiff';
                const isCurrencyMode = this.currentSortBy === 'summary';

                return products.map(({ product, price: history }) => {
                    const recommendedPrice = Number.isFinite(Number(product?.price)) ? Number(product.price) : null;

                    const records = Array.isArray(history) ? history : [];
                    const sortedHistory = records
                        .map(entry => ({
                            date: entry.date,
                            timestamp: new Date(entry.date).getTime(),
                            stores: Object.fromEntries(Object.entries(entry).filter(([key]) => key !== 'date')),
                        }))
                        .sort((a, b) => a.timestamp - b.timestamp);

                    const storeNames = Array.from(
                        sortedHistory.reduce((setAcc, entry) => {
                            Object.keys(entry.stores).forEach(storeKey => setAcc.add(storeKey));
                            return setAcc;
                        }, new Set())
                    ).sort();

                    const storeBucketsMap = {};
                    storeNames.forEach(storeName => (storeBucketsMap[storeName] = {}));

                    const minTs = sortedHistory[0]?.timestamp ?? null;
                    const maxTs = sortedHistory[sortedHistory.length - 1]?.timestamp ?? null;

                    sortedHistory.forEach(item => {
                        const bucketStartTs = getBucketStartTimestamp(item.timestamp, this.currentGroupBy);
                        storeNames.forEach(storeName => {
                            const rawValue = item.stores[storeName];
                            const numericValue = Number(rawValue);
                            if (rawValue == null || Number.isNaN(numericValue)) return;
                            if (!storeBucketsMap[storeName][bucketStartTs]) {
                                storeBucketsMap[storeName][bucketStartTs] = { sum: 0, count: 0 };
                            }
                            storeBucketsMap[storeName][bucketStartTs].sum += numericValue;
                            storeBucketsMap[storeName][bucketStartTs].count += 1;
                        });
                    });

                    const allBucketTimestamps = buildFullBuckets(minTs, maxTs, this.currentGroupBy);

                    const referenceLineSeries = {
                        name: this.$t('recommendedPrice'),
                        type: 'line',
                        smooth: false,
                        data: (() => {
                            if (!allBucketTimestamps.length) return [];
                            if (isPercentMode) {
                                return allBucketTimestamps.map(bucketTs => [bucketTs, 0]);
                            }
                            if (recommendedPrice != null) {
                                return allBucketTimestamps.map(bucketTs => [bucketTs, recommendedPrice]);
                            }
                            return [];
                        })(),
                        lineStyle: { color: 'black' },
                        itemStyle: { color: 'black' },
                        symbol: 'none',
                        areaStyle: null,
                        connectNulls: true,
                    };

                    const storeSeries = storeNames.map(storeName => {
                        const buckets = storeBucketsMap[storeName];

                        const dataPoints = allBucketTimestamps.map(bucketTs => {
                            const bucket = buckets[bucketTs];
                            const averagePrice = bucket ? bucket.sum / bucket.count : null;

                            const deviationValue = averagePrice != null && recommendedPrice != null ? averagePrice - recommendedPrice : null;

                            const deviationPercent = deviationValue != null && recommendedPrice ? (deviationValue / recommendedPrice) * 100 : null;

                            const yValue = isPercentMode ? deviationPercent : averagePrice;

                            return {
                                value: [bucketTs, yValue],
                                storeName,
                                rawPrice: averagePrice,
                                deviationValue,
                                deviationPercent,
                                recommendedPrice,
                            };
                        });

                        return {
                            name: storeName,
                            type: 'line',
                            data: dataPoints,
                            areaStyle: null,
                            connectNulls: false,
                        };
                    });

                    const chartOptions = Object.freeze({
                        grid: { top: '80px' },
                        title: {
                            text: product.name,
                            subtext: `EAN: ${product.ean}`,
                            textStyle: { fontSize: 14 },
                        },
                        tooltip: {
                            trigger: 'item',
                            valueFormatter: val => {
                                if (isPercentMode) return this.$format.percent(val);
                                if (isCurrencyMode) return this.$format.currency(val);
                                return val;
                            },
                        },
                        xAxis: {
                            boundaryGap: false,
                        },
                        yAxis: {
                            type: 'value',
                            axisLabel: {
                                formatter: val => {
                                    if (isPercentMode) return this.$format.percent(val);
                                    if (isCurrencyMode) return this.$format.currency(val, null, null, 0);
                                    return val;
                                },
                            },
                        },
                        legend: {
                            data: [this.$t('recommendedPrice'), ...storeNames],
                        },
                        series: [referenceLineSeries, ...storeSeries],
                    });

                    return { chartOptions };
                });
            },
        },
    };
</script>



<style lang="scss" scoped>
    .edit-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 10;
    }
</style>
